// src/pages/MyRankings.js
import React, { useEffect, useMemo, useState } from 'react';
import axios, { PUBLIC_BASE } from '../utils/axiosInstance';
import ListCard from '../components/ListCard';

function dedupeLatestByListId(arr = []) {
  // prefer newest by updatedAt/createdAt; fall back to last occurrence
  const byId = new Map();
  arr.forEach((r, idx) => {
    const key = String(r.listId);
    const ts = new Date(r.updatedAt || r.createdAt || 0).getTime() || idx;
    const cur = byId.get(key);
    if (!cur || ts >= cur.ts) byId.set(key, { ts, rec: r });
  });
  return Array.from(byId.values()).map((x) => x.rec);
}

// normalize a DB image value to a browser-usable URL
const toImageUrl = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && /^https?:\/\//i.test(raw)) return raw; // absolute URL
  const s = String(raw);
  const path = s.startsWith('/') ? s : `/${s}`;
  return `${PUBLIC_BASE}${path}`; // e.g. http://localhost:5050 + /uploads/...
};

const MyRankings = ({ embedded = false }) => {  // ⬅️ tiny prop
  const [mine, setMine] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');     // search query

  const username = localStorage.getItem('username');

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        setErr('');
        setLoading(true);
        const [rRes, lRes] = await Promise.all([
          axios.get(`/rankings/user/${username}`),
          axios.get('/rankings'),
        ]);
        if (!alive) return;
        setMine(dedupeLatestByListId(rRes.data || []));
        setLists(lRes.data || []);
      } catch (e) {
        console.error('Failed to fetch my rankings', e);
        if (alive) setErr('Failed to load your rankings.');
      } finally {
        if (alive) setLoading(false);
      }
    };
    if (username) run();
    return () => {
      alive = false;
    };
  }, [username]);

  const cards = useMemo(() => {
    // index lists by id for quick lookups
    const meta = new Map((lists || []).map((l) => [String(l._id || l.id), l]));
    return mine.map((r) => {
      const m = meta.get(String(r.listId)) || {};
      const rawImg =
        m.image ?? m.cover ?? m.imageUrl ?? m.imageURL ?? m.thumbnail ?? '';
      return {
        id: r.listId,
        title: m.listName || m.name || 'Unknown List',
        subtitle: m.createdBy || 'unknown',
        imageUrl: toImageUrl(rawImg),
        listName: m.listName || m.name,
      };
    });
  }, [mine, lists]);

  // Live filter by title (case-insensitive)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => (c.title || '').toLowerCase().includes(q));
  }, [cards, query]);

  const onClear = () => setQuery('');

  const RankingsInner = (
    <div className="scroll-body myrankings-page">
      {err && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{err}</div>}

      {/* Search bar */}
      <div
        className="myrankings-search-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          maxWidth: 520,
          margin: '0px auto 10px',
        }}
      >
        <input
          className="input input-outline-black"
          type="text"
          value={query}
          placeholder="Search your lists…"
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search my rankings"
          style={{ height: 40, flex: 1 }}
        />
        <button
          className="btn-outline-black"
          onClick={onClear}
          disabled={!query}
          style={{ height: 40, padding: '0 14px' }}
        >
          Clear
        </button>
      </div>

      {/* Divider aligned to grid width */}
      <div className="grid-divider" aria-hidden="true" />

      <div className="grid">
        {filtered.map((it) => (
          <ListCard key={it.id} {...it} to={`/my-rankings/${it.id}`} />
        ))}
      </div>

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: '#777', marginTop: 24 }}>
          {query ? <>No lists match “{query}”.</> : <>You haven’t ranked any lists yet.</>}
        </div>
      )}
    </div>
  );

  // When embedded under Profile, avoid adding an extra scroll-shell
  if (embedded) return RankingsInner;

  return (
    <div className="scroll-shell">
      {RankingsInner}
    </div>
  );
};

export default MyRankings;
